import React from 'react';
import { TeamInfo, PitColumn, PitSlot, KartRating } from '../types';
import { usePitAssignments, PIT_COLUMNS } from '../hooks/usePitAssignments';
import { PitBoardStore } from '../hooks/pitBoardStore';
import { prefersTouchUi } from '../hooks/useTouchUi';
import { apexPitSeconds, formatElapsed } from '../analytics/elapsed';
import { useAssignLayout } from '../hooks/useAssignLayout';
import { useColumnOrder } from '../hooks/useColumnOrder';
import './PitSidebar.css';

interface PitSidebarProps {
  teams: Map<string, TeamInfo>;
  isVisible: boolean;
  onToggle: () => void;
  /** Overrides the clock and storage slot; replay supplies both. */
  now?: () => number;
  storageKey?: string;
  /**
   * Where the board lives. The live screen passes a server-backed store so the
   * whole pit wall shares one board; replay leaves it out and stays local.
   */
  store?: PitBoardStore;
  /**
   * 'drawer' is the bar fixed to the bottom of the replay screen, which opens
   * and closes. 'page' fills whatever it is given and drops the chrome that
   * only makes sense for a drawer — no toggle, no fullscreen, nothing to open.
   */
  variant?: 'drawer' | 'page';
}

const getRatingIcon = (rating: KartRating): string => {
  switch (rating) {
    case 'fast': return '🚀';
    case 'medium': return '⚡';
    case 'slow': return '🐌';
    default: return '❓';
  }
};

const RATING_CHOICES: KartRating[] = ['slow', 'medium', 'fast'];

/** pro / gold / silver as Apex sends them; anything else is shown as given. */
const categoryLabel = (category: string): string => {
  switch (category) {
    case 'pro': return 'Pro';
    case 'gold': return 'Gold';
    case 'silver': return 'Silver';
    default: return category;
  }
};

const getRatingText = (rating: KartRating): string => {
  switch (rating) {
    case 'fast': return 'Fast';
    case 'medium': return 'Medium';
    case 'slow': return 'Slow';
    default: return 'Unknown';
  }
};

/**
 * These wipe the board for everyone on the pit wall in one tap, and on a tablet
 * they sit next to much bigger, closer targets than they used to.
 */
const confirmDestructive = (message: string): boolean =>
  typeof window.confirm !== 'function' || window.confirm(message);

const PitSidebar: React.FC<PitSidebarProps> = ({
  teams,
  isVisible,
  onToggle,
  now,
  storageKey,
  store,
  variant = 'drawer',
}) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [selectedSlotId, setSelectedSlotId] = React.useState<string | null>(null);
  const { layout: assignLayout, toggle: toggleAssignLayout } = useAssignLayout();
  // Left-to-right order of the lanes, remembered per device.
  const { order: columnOrder, move: moveColumn } = useColumnOrder();
  // In 'tap' mode only the card that was tapped shows its buttons.
  const [revealedTeamId, setRevealedTeamId] = React.useState<string | null>(null);
  // Which lane a phone is showing. Three lanes will not fit 390px side by side,
  // so in portrait the board shows one and these tabs swap it. Above that
  // breakpoint the tabs are hidden and every lane shows, so this sits unused.
  const [phoneLane, setPhoneLane] = React.useState<PitColumn>(columnOrder[0]);

  const {
    board,
    assignKartToColumn,
    addBlankKartToColumn,
    removeSlot,
    setSlotRating,
    moveSlot,
    clearColumn,
    getUnassignedPitTeams,
    dismissFromQueue,
    getSlotPitStatus
  } = usePitAssignments(teams, { now, storageKey, store });

  const unassignedTeams = getUnassignedPitTeams();

  // Times in the box count up on their own. Replay passes its virtual clock, so
  // read it through a ref: callers pass an inline arrow, which would otherwise
  // rebuild the interval on every render.
  const clockRef = React.useRef(now);
  clockRef.current = now;
  const [tick, setTick] = React.useState(() => (now ? now() : Date.now()));
  React.useEffect(() => {
    const id = setInterval(
      () => setTick(clockRef.current ? clockRef.current() : Date.now()),
      1000,
    );
    return () => clearInterval(id);
  }, []);

  // The 45px drawer has no room for finger-sized targets, so on a touch device
  // opening the board goes straight to the full view.
  React.useEffect(() => {
    if (variant === 'page') return;
    if (isVisible && prefersTouchUi()) setIsFullscreen(true);
  }, [isVisible, variant]);

  // Typed kart entry: a number goes in, then a column is picked for it.
  const [kartEntry, setKartEntry] = React.useState('');
  const [pendingKart, setPendingKart] = React.useState<string | null>(null);
  const kartEntryRef = React.useRef<HTMLInputElement>(null);

  const submitKartEntry = (e: React.FormEvent) => {
    e.preventDefault();
    const number = kartEntry.trim();
    if (number) setPendingKart(number);
  };

  /**
   * Puts the typed kart in a column. A kart already on the board moves rather
   * than appearing twice; anything else is a new box with that number.
   */
  const placeKart = (kartNumber: string, column: PitColumn) => {
    const existing = PIT_COLUMNS.flatMap(c => board[c]).find(s => s.kartNumber === kartNumber);
    if (existing) {
      if (existing.column !== column) moveSlot(existing.slotId, column);
    } else {
      addBlankKartToColumn(kartNumber, column);
    }
    setPendingKart(null);
    setKartEntry('');
    kartEntryRef.current?.focus();
  };

  const handleAddBlankKart = (column: PitColumn) => {
    // Numbered from what is already on the board rather than from the clock, so a
    // paused replay (where the clock does not advance) cannot mint the same number
    // twice.
    const used = new Set(
      [...board.red, ...board.green, ...board.blue].map(s => s.kartNumber),
    );
    let n = board[column].length + 1;
    while (used.has(`${n}`)) n++;

    addBlankKartToColumn(`${n}`, column);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen && !isVisible) {
      onToggle(); // Auto-open sidebar when going fullscreen
    }
  };

  // Handle escape key to exit fullscreen
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape means "cancel the current thing": drop a pending move before
      // it means "leave fullscreen".
      if (selectedSlotId) {
        setSelectedSlotId(null);
        return;
      }
      if (variant !== 'page' && isFullscreen) setIsFullscreen(false);
    };

    // Listen whenever there is something to cancel; only fullscreen locks scroll.
    if (isFullscreen || selectedSlotId) {
      document.addEventListener('keydown', handleEscape);
    }
    if (variant !== 'page') document.body.style.overflow = isFullscreen ? 'hidden' : 'unset';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen, selectedSlotId, variant]);

  /**
   * How long a kart has been in the pit. Apex's own stopwatch where it is
   * sending one, our timing of the *in transition otherwise.
   */
  const pitTimeOf = (team: TeamInfo | undefined): string | null => {
    if (!team) return null;
    const apex = apexPitSeconds(team.onTrackTime);
    if (apex != null) return formatElapsed(apex * 1000);
    if (team.pitEntryTime != null) return formatElapsed(tick - team.pitEntryTime);
    return null;
  };

  const renderKartCard = (slot: PitSlot) => {
    const occupant = slot.occupant;
    const isCurrentlyInPit = getSlotPitStatus(slot);
    const isSelected = slot.slotId === selectedSlotId;
    const team = occupant ? teams.get(occupant.teamId) : undefined;
    const teamName = team?.teamName?.trim() || '';

    // Apex's own pit stopwatch first: it is the track's clock, so it is right
    // even when we joined mid-stop and never saw the kart go in. Our own timing
    // of the *in transition is the fallback, and time in this box after that.
    // An empty box has no kart, so it has no clock worth watching — a count-up
    // there is just noise on a board people read at a glance.
    const pitTime = isCurrentlyInPit ? pitTimeOf(team) : null;
    const elapsed = occupant ? (pitTime ?? formatElapsed(tick - occupant.since)) : null;
    const elapsedLabel = pitTime ? 'Time in the pit' : 'Time in this box';
    const cardClasses = [
      'kart-card',
      occupant ? (isCurrentlyInPit ? 'in-pit' : 'out-of-pit') : 'blank-kart',
      isSelected ? 'selected' : '',
    ].filter(Boolean).join(' ');

    const toggleSelected = () =>
      setSelectedSlotId(current => (current === slot.slotId ? null : slot.slotId));

    return (
      // Not a <button>: it contains the remove button, and nesting those is
      // invalid. Tapping picks the box up; tapping a column then drops it.
      <div
        key={slot.slotId}
        className={cardClasses}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        aria-label={`Kart ${slot.kartNumber}${occupant ? `, ${occupant.driver}` : ', empty box'}`}
        onClick={toggleSelected}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleSelected();
          }
        }}
      >
        <div className="kart-header">
          <span className="kart-number">#{slot.kartNumber}</span>
          <div className="kart-status">
            {occupant ? (
              <span className="pit-status">{isCurrentlyInPit ? '🔴 IN PIT' : '🟢 ON TRACK'}</span>
            ) : (
              <span className="blank-status">⭐ FREE</span>
            )}
          </div>
          {elapsed && (
            <span className="kart-elapsed" aria-label={elapsedLabel}>
              {elapsed}
            </span>
          )}
          <button
            className="remove-btn"
            onClick={event => {
              // Without this, removing a box also selects the card it was on.
              event.stopPropagation();
              removeSlot(slot.slotId);
            }}
            title="Remove this box from pit tracking"
            aria-label={`Remove kart ${slot.kartNumber} from pit tracking`}
          >
            ×
          </button>
        </div>
        <div className="kart-meta">
          <span className="kart-identity">
            {teamName && <span className="kart-team">{teamName}</span>}
            <span className="kart-driver">{occupant ? occupant.driver : 'Empty'}</span>
            {!occupant && slot.lastOccupant?.category && (
              // A free kart's pace was set by whoever just got out of it, and
              // their class says how much to trust the number.
              <span
                className={`kart-category kart-category--${slot.lastOccupant.category}`}
                title={`Last driven by ${slot.lastOccupant.driver || 'unknown'} (${categoryLabel(slot.lastOccupant.category)})`}
                aria-label={`Last driver category ${categoryLabel(slot.lastOccupant.category)}`}
              >
                {categoryLabel(slot.lastOccupant.category)}
              </span>
            )}
          </span>
          <span className="kart-pace">
            {occupant ? (
              <span className="rating-icon" aria-label={getRatingText(slot.rating)}>
                {getRatingIcon(slot.rating)}
              </span>
            ) : (
              // A free kart's pace is whatever the last driver reported. Three
              // pressable icons rather than a cycle: one tap lands on the
              // rating you meant.
              <span
                className="rating-picker"
                role="group"
                aria-label={`Pace of kart ${slot.kartNumber}`}
              >
                {RATING_CHOICES.map(rating => (
                  <button
                    key={rating}
                    type="button"
                    className={`rating-btn${slot.rating === rating ? ' is-current' : ''}`}
                    aria-pressed={slot.rating === rating}
                    aria-label={`Rate kart ${slot.kartNumber} ${getRatingText(rating).toLowerCase()}`}
                    title={getRatingText(rating)}
                    onClick={event => {
                      // The card is a control too; without this, rating also
                      // picks the box up.
                      event.stopPropagation();
                      setSlotRating(slot.slotId, rating);
                    }}
                  >
                    {getRatingIcon(rating)}
                  </button>
                ))}
              </span>
            )}
            <span className="kart-time">{slot.averageTime || 'N/A'}</span>
          </span>
        </div>
      </div>
    );
  };

  // The colour comes from the column's own class in CSS, not an inline hex —
  // an inline value cannot follow the theme.
  const renderColumnHeader = (column: PitColumn) => {
    const position = columnOrder.indexOf(column);
    const label = column[0].toUpperCase() + column.slice(1);
    return (
      <div className="column-header">
        <div className="column-title">
          {column.toUpperCase()} ({board[column].length})
        </div>
        <div className="column-actions">
          {/* Arrows swap the lane with its neighbour, so the wall can put the
              lane nearest the pit exit wherever it reads best. */}
          <button
            className="move-column-btn"
            onClick={() => moveColumn(column, -1)}
            disabled={position === 0}
            title={`Move ${label} column left`}
            aria-label={`Move ${label} column left`}
          >
            ◀
          </button>
          <button
            className="move-column-btn"
            onClick={() => moveColumn(column, 1)}
            disabled={position === columnOrder.length - 1}
            title={`Move ${label} column right`}
            aria-label={`Move ${label} column right`}
          >
            ▶
          </button>
          {board[column].length > 0 && (
            <button
              className="clear-column-btn"
              onClick={() => {
                if (confirmDestructive(`Clear the ${column} column? This cannot be undone.`)) {
                  clearColumn(column);
                }
              }}
              title={`Clear ${column} column`}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  };

  const allSlots = [...board.red, ...board.green, ...board.blue];
  const occupiedCount = allSlots.filter(s => s.occupant).length;
  const selectedColumn = selectedSlotId
    ? PIT_COLUMNS.find(c => board[c].some(s => s.slotId === selectedSlotId)) ?? null
    : null;

  // The board is shared: a kart can leave the pit, or somebody else on the wall
  // can remove the very box we have picked up.
  React.useEffect(() => {
    if (!selectedSlotId) return;
    const stillThere = PIT_COLUMNS.some(c =>
      board[c].some(s => s.slotId === selectedSlotId),
    );
    if (!stillThere) setSelectedSlotId(null);
  }, [board, selectedSlotId]);

  /** Drop zone shown in the other columns while a box is picked up. */
  const renderMoveTarget = (column: PitColumn) => {
    if (!selectedSlotId || selectedColumn === column) return null;
    return (
      <button
        className="move-target-btn"
        onClick={() => {
          moveSlot(selectedSlotId, column);
          setSelectedSlotId(null);
        }}
      >
        Move here
      </button>
    );
  };

  const isPage = variant === 'page';
  const open = isPage || isVisible;

  return (
    <div
      className={
        isPage
          ? 'pit-sidebar pit-sidebar--page'
          : `pit-sidebar ${isVisible ? 'visible' : ''} ${isFullscreen ? 'fullscreen' : ''}`
      }
    >
      {!isPage && (
        <div className="pit-header">
          <h3>Pit Lane Monitor</h3>
          <div className="header-controls">
            <button
              className="fullscreen-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Mode'}
            >
              {isFullscreen ? '⤓' : '⤢'}
            </button>
            {!isFullscreen && (
              <button className="toggle-btn" onClick={onToggle}>
                {isVisible ? '↓' : '↑'}
              </button>
            )}
          </div>
        </div>
      )}

      {open && (
        <div
          className={`pit-content${unassignedTeams.length === 0 ? ' pit-content--no-pool' : ''}`}
        >
          {/* Top row with controls */}
          <div className="pit-top-row">
            {/* Statistics */}
            <div className="pit-stats">
              <div className="stat">
                <span className="stat-label">Assigned:</span>
                <span className="stat-value">{occupiedCount}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Unassigned:</span>
                <span className="stat-value">{unassignedTeams.length}</span>
              </div>
            </div>

            {/* Adding a box: the one thing done often enough to lead the row. */}
            <div className="blank-kart-section">
              <h4>Add Kart</h4>
              {pendingKart === null ? (
                <form className="add-kart-form" onSubmit={submitKartEntry}>
                  <input
                    ref={kartEntryRef}
                    className="add-kart-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="Kart #"
                    aria-label="Kart number"
                    value={kartEntry}
                    onChange={e => setKartEntry(e.target.value)}
                  />
                  <button type="submit" className="add-kart-submit" disabled={!kartEntry.trim()}>
                    Add
                  </button>
                </form>
              ) : (
                <div className="add-kart-choice" role="group" aria-label={`Column for kart ${pendingKart}`}>
                  <span className="add-kart-pending">Kart #{pendingKart} →</span>
                  {columnOrder.map(column => (
                    <button
                      key={column}
                      type="button"
                      className={`blank-btn ${column}`}
                      onClick={() => placeKart(pendingKart, column)}
                      aria-label={`Put kart ${pendingKart} in ${column}`}
                    >
                      {column[0].toUpperCase() + column.slice(1)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="add-kart-cancel"
                    onClick={() => setPendingKart(null)}
                    aria-label="Cancel adding kart"
                  >
                    ✕
                  </button>
                </div>
              )}
              <div className="blank-kart-buttons">
                <button 
                  className="blank-btn red"
                  onClick={() => handleAddBlankKart('red')}
                  title="Add blank kart to Red"
                >
                  + Red
                </button>
                <button 
                  className="blank-btn green"
                  onClick={() => handleAddBlankKart('green')}
                  title="Add blank kart to Green"
                >
                  + Green
                </button>
                <button 
                  className="blank-btn blue"
                  onClick={() => handleAddBlankKart('blue')}
                  title="Add blank kart to Blue"
                >
                  + Blue
                </button>
              </div>
            </div>

            <button
              className="assign-layout-btn"
              onClick={toggleAssignLayout}
              aria-pressed={assignLayout === 'tap'}
              title={
                assignLayout === 'row'
                  ? 'Buttons are always shown. Switch to showing them on tap, which fits more karts in the queue.'
                  : 'Buttons appear when a kart is tapped. Switch to always showing them.'
              }
            >
              {assignLayout === 'row' ? 'Buttons: always' : 'Buttons: on tap'}
            </button>
          </div>

          {/* Unassigned karts */}
          <div className="unassigned-section">
            <h4>Unassigned Karts in Pit</h4>
            <div className="unassigned-karts">
              {unassignedTeams.length === 0 ? (
                <p className="unassigned-empty">No karts waiting in the pit.</p>
              ) : (
                unassignedTeams.map(team => {
                  const shown = assignLayout === 'row' || revealedTeamId === team.id;
                  const wait = pitTimeOf(team);
                  return (
                    <div
                      key={team.id}
                      className={`unassigned-kart${shown ? ' is-open' : ''}`}
                      // In 'tap' mode the card itself is the control that
                      // reveals the buttons; in 'row' mode there is nothing to
                      // reveal, so it stays inert.
                      {...(assignLayout === 'tap'
                        ? {
                            role: 'button' as const,
                            tabIndex: 0,
                            'aria-expanded': shown,
                            onClick: () =>
                              setRevealedTeamId(id => (id === team.id ? null : team.id)),
                            onKeyDown: (event: React.KeyboardEvent) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setRevealedTeamId(id => (id === team.id ? null : team.id));
                              }
                            },
                          }
                        : {})}
                    >
                      <div className="unassigned-kart-head">
                        {/* Number and clock only. The name is on the board card
                            once assigned; here it just made cards uneven. */}
                        <div className="kart-info">
                          <span className="kart-number">#{team.kartNumber}</span>
                        </div>
                        {wait && (
                          <span className="kart-elapsed" aria-label="Time in the pit">
                            {wait}
                          </span>
                        )}
                        <button
                          type="button"
                          className="remove-btn queue-remove-btn"
                          onClick={event => {
                            // In tap mode the card is a control; without this,
                            // removing also opens it.
                            event.stopPropagation();
                            dismissFromQueue(team.id);
                            setRevealedTeamId(id => (id === team.id ? null : id));
                          }}
                          title="Remove from the queue until the kart leaves the pit"
                          aria-label={`Remove kart ${team.kartNumber} from the queue`}
                        >
                          ×
                        </button>
                      </div>

                      {shown && (
                        <div className="assign-buttons">
                          {columnOrder.map(column => (
                            <button
                              key={column}
                              className={`assign-btn ${column}`}
                              onClick={event => {
                                // The card is a control in 'tap' mode; without
                                // this, assigning also toggles it shut.
                                event.stopPropagation();
                                assignKartToColumn(team.id, column);
                                setRevealedTeamId(null);
                              }}
                              title={`Assign to ${column[0].toUpperCase()}${column.slice(1)}`}
                              aria-label={`Assign kart ${team.kartNumber} to ${column}`}
                            >
                              {column[0].toUpperCase()}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Lane switcher — only visible on a phone in portrait. */}
          <div className="pit-lane-tabs" role="tablist" aria-label="Pit lane">
            {columnOrder.map(column => (
              <button
                key={column}
                role="tab"
                aria-selected={phoneLane === column}
                className={`pit-lane-tab ${column}${phoneLane === column ? ' is-active' : ''}`}
                onClick={() => setPhoneLane(column)}
              >
                {column.toUpperCase()} ({board[column].length})
              </button>
            ))}
          </div>

          {/* Colour columns, left to right in the order this device chose. */}
          <div className="pit-columns" data-active-lane={phoneLane}>
            {columnOrder.map(column => (
              <div key={column} className={`pit-column ${column}-column`}>
                {renderColumnHeader(column)}
                {renderMoveTarget(column)}
                <div className="kart-cards">
                  {board[column].map(slot => renderKartCard(slot))}
                </div>
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
};

export default PitSidebar; 